import { api } from '@invisoutlet/shared/api';
import type { AlertDisplay } from '@invisoutlet/shared/components';
import { BuildingCard, type BuildingSummary } from '@invisoutlet/shared/components';
import { useAuthContext } from '@invisoutlet/shared/context';
import { useAllDevices, useWebSocketStatus } from '@invisoutlet/shared/hooks';
import type { Device, Entity, IsoString } from '@invisoutlet/shared/types';
import { aggregateSensors, generateMockSensorData, hasAddOn } from '@invisoutlet/shared/utils';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AISummaryPanel } from '../components/ai-summary-panel.js';
import { BuildingSearchDropdown } from '../components/building-search-dropdown.js';
import { EnterpriseLayout } from '../components/enterprise-layout.js';
import { MultiSelectDropdown } from '../components/multi-select-dropdown.js';

// ─── Mock alerts ──────────────────────────────────────────────────────────────
// Placeholder until GET /api/entities/:entityId/alerts is wired up.

const MOCK_ALERTS: AlertDisplay[] = [
  {
    id: 'mock-alert-1',
    severity: 'critical',
    message: 'Temperature exceeded 85°F (current: 91°F)',
    deviceName: 'Room 204 Outlet',
    buildingName: 'Downtown Hotel',
    room: 'Room 204',
    createdAt: new Date(Date.now() - 2 * 60 * 1_000).toISOString() as IsoString,
    status: 'active',
  },
  {
    id: 'mock-alert-2',
    severity: 'warning',
    message: 'Humidity above 70% (current: 74%)',
    deviceName: 'Lobby Outlet',
    buildingName: 'Airport Marriott',
    room: 'Lobby',
    createdAt: new Date(Date.now() - 15 * 60 * 1_000).toISOString() as IsoString,
    status: 'active',
  },
];

// ─── Empty state ──────────────────────────────────────────────────────────────

function NoBuildingsState() {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-bg-elevated">
        <svg
          className="h-8 w-8 text-text-secondary"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M3 21h18M3 7l9-4 9 4M4 7v14M20 7v14M8 10v2M8 14v2M12 10v2M12 14v2M16 10v2M16 14v2" />
        </svg>
      </div>
      <h2 className="mb-2 text-lg font-semibold text-text-primary">No buildings configured yet</h2>
      <p className="max-w-sm text-sm text-text-secondary">
        Contact your Intecular administrator to set up your properties.
      </p>
    </div>
  );
}

// ─── Error state ──────────────────────────────────────────────────────────────

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <p className="mb-4 text-sm text-danger">Failed to load entity data.</p>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-accent/50"
      >
        Retry
      </button>
    </div>
  );
}

// ─── Skeleton grid ────────────────────────────────────────────────────────────

function BuildingCardSkeleton() {
  return (
    <div className="animate-pulse rounded-xl border border-border bg-bg-primary p-5">
      <div className="mb-2 flex items-start justify-between">
        <div className="h-5 w-2/3 rounded bg-bg-elevated" />
        <div className="h-5 w-5 rounded-full bg-bg-elevated" />
      </div>
      <div className="mb-4 h-4 w-1/3 rounded bg-bg-elevated" />
      <div className="mb-1 flex justify-between">
        <div className="h-4 w-1/4 rounded bg-bg-elevated" />
        <div className="h-4 w-1/4 rounded bg-bg-elevated" />
      </div>
      <div className="h-1.5 w-full rounded-full bg-bg-elevated" />
      <div className="mt-3 flex gap-3">
        <div className="h-3 w-16 rounded bg-bg-elevated" />
        <div className="h-3 w-16 rounded bg-bg-elevated" />
        <div className="h-3 w-16 rounded bg-bg-elevated" />
      </div>
      <div className="mt-4 h-3 w-1/3 rounded bg-bg-elevated" />
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function BuildingOverviewPage() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const entityId = user!.entityId!;

  const [alerts, setAlerts] = useState<AlertDisplay[]>(MOCK_ALERTS);
  const [filterBuildingId, setFilterBuildingId] = useState('');
  const [filterCountries, setFilterCountries] = useState<string[]>([]);
  const [filterStates, setFilterStates] = useState<string[]>([]);
  const [filterCities, setFilterCities] = useState<string[]>([]);

  const {
    data: entityResponse,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['entity', entityId],
    queryFn: () => api.get<Entity>(`/api/entities/${entityId}`),
    enabled: !!entityId,
  });

  const entity = entityResponse?.data;

  // Devices — same queryKey as table-view/floor-view so React Query reuses the cache
  const { data: devicesRes } = useQuery({
    queryKey: ['devices', entityId],
    queryFn: () => api.get<Device[]>(`/api/entities/${entityId}/devices`),
    enabled: !!entityId,
  });

  // Live sensor data from WebSocket store (empty map when not connected)
  const liveDevices = useAllDevices();
  const { connected } = useWebSocketStatus();

  const temperatureUnit = entity?.preference.temperatureUnit ?? '°F';

  // Filter buildings by user's building access (empty = all)
  const accessibleBuildings = useMemo(() => {
    if (!entity) return [];
    const all = entity.buildings ?? [];
    if (user!.buildings.length === 0) return all;
    return all.filter((b) => user!.buildings.includes(b.id));
  }, [entity, user]);

  // Unique sorted filter options derived from accessible buildings
  const availableCountries = useMemo(
    () => [...new Set(accessibleBuildings.map((b) => b.address.country).filter(Boolean))].sort(),
    [accessibleBuildings],
  );
  const availableStates = useMemo(
    () => [...new Set(accessibleBuildings.map((b) => b.address.state).filter(Boolean))].sort(),
    [accessibleBuildings],
  );
  const availableCities = useMemo(
    () => [...new Set(accessibleBuildings.map((b) => b.address.city).filter(Boolean))].sort(),
    [accessibleBuildings],
  );

  // Apply location + building filters
  const displayedBuildings = useMemo(() => {
    return accessibleBuildings.filter((b) => {
      if (filterBuildingId && b.id !== filterBuildingId) return false;
      if (filterCountries.length && !filterCountries.includes(b.address.country)) return false;
      if (filterStates.length && !filterStates.includes(b.address.state)) return false;
      if (filterCities.length && !filterCities.includes(b.address.city)) return false;
      return true;
    });
  }, [accessibleBuildings, filterBuildingId, filterCountries, filterStates, filterCities]);

  const hasActiveFilters =
    filterBuildingId !== '' ||
    filterCountries.length > 0 ||
    filterStates.length > 0 ||
    filterCities.length > 0;

  function clearAllFilters() {
    setFilterBuildingId('');
    setFilterCountries([]);
    setFilterStates([]);
    setFilterCities([]);
  }

  // Compute aggregated summary per building from real device+sensor data
  const buildingSummaries = useMemo(() => {
    const allDevices = devicesRes?.data ?? [];
    const summaries: Record<string, BuildingSummary> = {};

    for (const building of accessibleBuildings) {
      const buildingDevices = allDevices.filter((d) => d.building === building.id && d.deviceId);
      const sensors = buildingDevices.map(
        (d) => liveDevices[d.deviceId] ?? (connected ? null : generateMockSensorData(d.deviceId)),
      );
      const agg = aggregateSensors(sensors, temperatureUnit);
      const allPending = connected && sensors.every((s) => s === null) && sensors.length > 0;
      const readySensors = sensors.filter((s): s is NonNullable<typeof s> => s !== null);
      const newest =
        readySensors.length > 0
          ? ([...readySensors].sort(
              (a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime(),
            )[0]?.lastUpdated ?? null)
          : null;
      summaries[building.id] = {
        onlineCount: readySensors.filter((s) => s.deviceStatus === 'online').length,
        totalCount: sensors.length,
        tempDisplay: allPending ? '…' : agg.tempDisplay,
        humidityDisplay: allPending ? '…' : agg.humidityDisplay,
        luxDisplay: allPending ? '…' : agg.luxDisplay,
        occupancyDisplay: allPending ? '…' : agg.occupancyDisplay,
        outletsDisplay: allPending ? '…' : agg.outletsDisplay,
        lastUpdated: newest as IsoString | null,
        // TODO: replace with real alert data from /api/entities/:entityId/alerts
        activeAlertCount: 0,
        highestAlertSeverity: null,
      };
    }

    return summaries;
  }, [devicesRes, accessibleBuildings, liveDevices, temperatureUnit, connected]);

  // Single-building shortcut — go straight to floor view
  useEffect(() => {
    if (!isLoading && !isError && accessibleBuildings.length === 1) {
      navigate(`/buildings/${accessibleBuildings[0].id}`, { replace: true });
    }
  }, [isLoading, isError, accessibleBuildings.length, accessibleBuildings[0]?.id, navigate]);

  function handleAcknowledge(alertId: string) {
    setAlerts((prev) =>
      prev.map((a) => (a.id === alertId ? { ...a, status: 'acknowledged' as const } : a)),
    );
  }

  return (
    <EnterpriseLayout
      alerts={alerts}
      title="Building Overview"
      onAcknowledgeAlert={handleAcknowledge}
    >
      <div className="mx-auto max-w-7xl">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-text-primary">Building Overview</h1>
          {entity && <p className="mt-1 text-sm text-text-secondary">{entity.name}</p>}
        </div>

        {entity && hasAddOn(entity, 'buildingAISummary') && !isLoading && !isError && (
          <AISummaryPanel
            entityId={entityId}
            displayedBuildings={displayedBuildings}
            buildingSummaries={buildingSummaries}
            temperatureUnit={temperatureUnit}
          />
        )}

        {isError && <ErrorState onRetry={() => void refetch()} />}

        {isLoading && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <BuildingCardSkeleton key={n} />
            ))}
          </div>
        )}

        {!isLoading && !isError && accessibleBuildings.length === 0 && <NoBuildingsState />}

        {/* Filter bar — only shown when there are buildings to filter */}
        {!isLoading && !isError && accessibleBuildings.length > 1 && (
          <div className="mb-4 flex flex-wrap items-center gap-3">
            {availableCountries.length > 1 && (
              <MultiSelectDropdown
                label="Country"
                options={availableCountries}
                selected={filterCountries}
                onChange={setFilterCountries}
              />
            )}
            {availableStates.length > 1 && (
              <MultiSelectDropdown
                label="State"
                options={availableStates}
                selected={filterStates}
                onChange={setFilterStates}
              />
            )}
            {availableCities.length > 1 && (
              <MultiSelectDropdown
                label="City"
                options={availableCities}
                selected={filterCities}
                onChange={setFilterCities}
              />
            )}
            <BuildingSearchDropdown
              buildings={accessibleBuildings}
              selected={filterBuildingId}
              onChange={setFilterBuildingId}
              nullable
              placeholder="Search buildings…"
            />
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearAllFilters}
                className="text-sm text-text-secondary underline-offset-2 hover:text-text-primary hover:underline focus:outline-none"
              >
                Clear all
              </button>
            )}
          </div>
        )}

        {!isLoading &&
          !isError &&
          accessibleBuildings.length > 1 &&
          displayedBuildings.length === 0 && (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <p className="mb-2 text-sm font-medium text-text-primary">
                No buildings match your filters
              </p>
              <button
                type="button"
                onClick={clearAllFilters}
                className="text-sm text-accent hover:underline focus:outline-none"
              >
                Clear filters
              </button>
            </div>
          )}

        {!isLoading &&
          !isError &&
          accessibleBuildings.length > 1 &&
          displayedBuildings.length > 0 && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {displayedBuildings.map((building) => (
                <BuildingCard
                  key={building.id}
                  buildingId={building.id}
                  building={building}
                  summary={
                    buildingSummaries[building.id] ?? {
                      onlineCount: 0,
                      totalCount: 0,
                      tempDisplay: '—',
                      humidityDisplay: '—',
                      luxDisplay: '—',
                      occupancyDisplay: '—',
                      outletsDisplay: '—',
                      lastUpdated: null,
                      activeAlertCount: 0,
                      highestAlertSeverity: null,
                    }
                  }
                  onClick={(id) => navigate(`/buildings/${id}`)}
                />
              ))}
            </div>
          )}
      </div>
    </EnterpriseLayout>
  );
}
